import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import styled from 'styled-components';

import DisabledFeatureConfigPanelContent from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel/DisabledFeatureConfigPanelContent';
import {
  DisabledFeatureConfigPanelForm,
  indexForOverride,
} from 'dashboard/apps/VerificationTemplateEditor/DisabledFeatureConfigPanel/util';
import {
  DisabledFeatureConfig,
  VerificationTemplateVersionGovernmentId,
} from 'dashboard/models/verification-template-version';
import Button, { IconButton } from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const PANEL_FOOTER_HEIGHT = 64;

const Form = styled.form`
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const disabledFeatureConfigToFormState = (
  disabledFeatureConfigIndex: number,
  disabledFeatureConfig: DisabledFeatureConfig,
): Partial<DisabledFeatureConfigPanelForm> => {
  const targetedIdClasses =
    disabledFeatureConfig['id-classes'] === 'default' ? [] : disabledFeatureConfig['id-classes'];
  const base: Partial<DisabledFeatureConfigPanelForm> = {
    index: disabledFeatureConfigIndex,
    countryCode: disabledFeatureConfig['country-code'],
    subdivision: disabledFeatureConfig.subdivision,
    idClasses: targetedIdClasses,
    disabledFeatures: disabledFeatureConfig['disabled-features'],
  };
  return base;
};

const formStateToDisabledFeatureConfig = (
  data: DisabledFeatureConfigPanelForm,
): DisabledFeatureConfig => {
  const targetedIdClasses =
    data.idClasses && data.idClasses.length > 0 ? data.idClasses : 'default';
  return {
    'country-code': data.countryCode,
    subdivision: data.subdivision,
    'id-classes': targetedIdClasses,
    'disabled-features': data.disabledFeatures,
    'max-age-to-disable': undefined,
  };
};

const errorRequired = { type: 'required', message: 'Required' };
const errorDuplicate = {
  type: 'duplicate',
  message: 'Duplicate config. Update the existing config instead.',
};

const validateDisabledFeatureConfigPanelForm = (
  data: DisabledFeatureConfigPanelForm,
  isNew: boolean,
  disabledFeatureConfigs: DisabledFeatureConfig[],
) => {
  const errors: FieldErrors<DisabledFeatureConfigPanelForm> = {};

  if (data.countryCode == null) {
    errors.countryCode = errorRequired;
  }

  if (data.subdivision == null) {
    errors.subdivision = errorRequired;
  }

  if (data.idClasses == null) {
    errors.idClasses = errorRequired;
  }

  const disabledFeatureConfig = formStateToDisabledFeatureConfig(data);
  const expectedIndex = indexForOverride({
    disabledFeatureConfigs,
    disabledFeatureConfigToFind: disabledFeatureConfig,
  });
  if (
    (isNew && expectedIndex >= 0) ||
    (!isNew && expectedIndex >= 0 && expectedIndex !== data.index)
  ) {
    // Discussed btwn Lauren and Lucas, best place to put this error is on the first field.
    errors.countryCode = errorDuplicate;
  }

  return Object.keys(errors).length === 0
    ? { values: data, errors: {} }
    : { values: {}, errors: errors };
};

interface DisabledFeatureConfigPanelProps {
  vtmplVersion: VerificationTemplateVersionGovernmentId;
  disabledFeatureConfigToEdit: DisabledFeatureConfig;
  closeDisabledFeatureConfig: () => void;
  addDisabledFeatureConfig: (disabledFeatureConfig: DisabledFeatureConfig) => void;
  updateDisabledFeatureConfig: (
    index: number,
    disabledFeatureConfig: DisabledFeatureConfig,
  ) => void;
  deleteDisabledFeatureConfig: () => void;
}

const DisabledFeatureConfigPanel = ({
  vtmplVersion,
  disabledFeatureConfigToEdit,
  closeDisabledFeatureConfig,
  addDisabledFeatureConfig,
  updateDisabledFeatureConfig,
  deleteDisabledFeatureConfig,
}: DisabledFeatureConfigPanelProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeDisabledFeatureConfig();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [closeDisabledFeatureConfig]);

  const isNew = disabledFeatureConfigToEdit['country-code'] == null;
  let defaultValues: Partial<DisabledFeatureConfigPanelForm> = {
    index: -1,
    countryCode: 'default',
    subdivision: 'default',
    idClasses: 'default',
    disabledFeatures: [],
  };

  const disabledFeatureConfigs = vtmplVersion.attributes['external-disabled-feature-configs'];

  if (!isNew) {
    const disabledFeatureConfigIndex = indexForOverride({
      disabledFeatureConfigs,
      disabledFeatureConfigToFind: disabledFeatureConfigToEdit,
    });

    if (disabledFeatureConfigIndex >= 0) {
      const disabledFeatureConfig = disabledFeatureConfigs[disabledFeatureConfigIndex];

      defaultValues = {
        ...defaultValues,
        ...disabledFeatureConfigToFormState(disabledFeatureConfigIndex, disabledFeatureConfig),
      };
    }
  }

  const formReturn = useFormSecurely<DisabledFeatureConfigPanelForm>({
    defaultValues,
    resolver: (data) => validateDisabledFeatureConfigPanelForm(data, isNew, disabledFeatureConfigs),
    mode: 'onChange',
  });
  const { handleSubmit } = formReturn;

  const handleSubmitForm = handleSubmit((data) => {
    const disabledFeatureConfig = formStateToDisabledFeatureConfig(data);
    if (isNew) {
      addDisabledFeatureConfig(disabledFeatureConfig);
    } else {
      updateDisabledFeatureConfig(data.index, disabledFeatureConfig);
    }
    closeDisabledFeatureConfig();
  });

  const handleCancel = () => {
    closeDisabledFeatureConfig();
  };

  const handleDelete = () => {
    deleteDisabledFeatureConfig();
    closeDisabledFeatureConfig();
  };

  return (
    <Form id='disabled-feature-config-panel__form' onSubmit={handleSubmitForm}>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            Configure{isNew && ' new'} jurisdiction specific features
          </Paragraph>
          Different jurisdictions can have different optimizations for compliance and performance
          reasons.
        </TitleContainer>
        <DisabledFeatureConfigPanelContent vtvType={vtmplVersion.type} formReturn={formReturn} />
      </BodyContainer>
      <FooterContainer>
        <HStack style={{ flexGrow: 1 }}>
          <Button
            id={`disabled-feature-config-panel__${isNew ? 'add' : 'update'}-button`}
            type='submit'
            width='auto'
            onClick={handleSubmitForm}
          >
            {isNew ? 'Add' : 'Update'}
          </Button>
          <Button
            id='disabled-feature-config-panel__close-button'
            width='auto'
            variant='basic'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </HStack>
        <IconButton
          id='disabled-feature-config-panel__delete-button'
          onClick={handleDelete}
          disabled={isNew}
        >
          <TrashIcon />
        </IconButton>
      </FooterContainer>
    </Form>
  );
};

export default DisabledFeatureConfigPanel;
